import * as THREE from 'three';
import {
  batchColor,
  batchIndirectIndex,
  cameraViewMatrix,
  color,
  diffuseColor,
  faceDirection,
  float,
  int,
  ivec2,
  materialEmissive,
  materialMetalness,
  materialRoughness,
  mix,
  normalView,
  normalWorldGeometry,
  positionViewDirection,
  positionWorld,
  select,
  sign,
  smoothstep,
  texture,
  textureLoad,
  textureSize,
  uniform,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import {
  MeshStandardNodeMaterial,
  type Node,
  type NodeBuilder,
  type UniformNode,
} from 'three/webgpu';
import type { MaterialPreset, PresetMaps } from './materials';

const RIM = color('#57b8ff');

export interface LayerUniforms {
  uGhost: UniformNode<'float', number>;
  uTint: UniformNode<'color', THREE.Color>;
  uTintAmount: UniformNode<'float', number>;
  // 0 flat IFC colours, 1 the preset's textured look
  uPbr: UniformNode<'float', number>;
}

export function layerUniforms(): LayerUniforms {
  return {
    uGhost: uniform(0),
    uTint: uniform(new THREE.Color()),
    uTintAmount: uniform(0),
    uPbr: uniform(1),
  };
}

// World-space projection with y up on every vertical face (the stock triplanarTexture puts y on
// u for x-facing walls, which stands brick courses on end), signs chosen so no face is mirrored
// and the tangent frame stays right-handed. Weights sharpen with the fourth power.
function triplanar(tiling: number) {
  const p = positionWorld.div(tiling);
  const n = normalWorldGeometry.normalize().mul(faceDirection);
  const s = sign(n);
  const a2 = n.mul(n);
  const a4 = a2.mul(a2);
  const w = a4.div(a4.x.add(a4.y).add(a4.z));
  const uvX = vec2(p.z.mul(s.x.negate()), p.y);
  const uvY = vec2(p.x, p.z.mul(s.y.negate()));
  const uvZ = vec2(p.x.mul(s.z), p.y);
  const sample = (map: THREE.Texture): Node<'vec4'> =>
    texture(map, uvX).mul(w.x).add(texture(map, uvY).mul(w.y)).add(texture(map, uvZ).mul(w.z));
  // UDN blend: each projection's tangent-space xy bends the geometric normal along that
  // projection's own u and v axes; the result goes to view space for the lighting.
  const normal = (map: THREE.Texture, strength: number): Node<'vec3'> => {
    const t = (uv: Node<'vec2'>) => texture(map, uv).xy.mul(2).sub(1).mul(strength);
    const tX = t(uvX);
    const tY = t(uvY);
    const tZ = t(uvZ);
    const nX = vec3(n.x, n.y.add(tX.y), n.z.sub(tX.x.mul(s.x)));
    const nY = vec3(n.x.add(tY.x), n.y, n.z.sub(tY.y.mul(s.y)));
    const nZ = vec3(n.x.add(tZ.x.mul(s.z)), n.y.add(tZ.y), n.z);
    const world = nX.mul(w.x).add(nY.mul(w.y)).add(nZ.mul(w.z)).normalize();
    return cameraViewMatrix.mul(vec4(world, 0)).xyz.normalize();
  };
  return { sample, normal };
}

// 'solid' draws the opaque instances of a batch, 'glass' the translucent ones of the same
// batch in a second pass, 'all' draws everything (batches that are translucent as a whole).
export type BatchPass = 'all' | 'solid' | 'glass';

export interface BatchLookOptions {
  pass: BatchPass;
  preset: MaterialPreset;
  maps: PresetMaps | null;
  color: THREE.Color;
  opacity: number;
  roughness: number;
  metalness: number;
  emissive: THREE.Color;
  emissiveIntensity: number;
}

// One state texel per instance: emissive rgb and strength. A negative strength means the
// instance shows its own look, so tint, ghost and the layer's emissive lift apply to it.
function stateTexel(state: THREE.Texture): Node<'vec4'> {
  const size = int((textureSize(textureLoad(state), int(0)) as unknown as Node<'ivec2'>).x);
  const i = int(batchIndirectIndex);
  return textureLoad(state, ivec2(i.mod(size), i.div(size)));
}

// The batch colour multiplies the base colour on its own (per-instance rgba); the state texel
// adds per-instance emissive and decides whether tint, ghost and the lift apply. uPbr blends an
// instance showing its own look from the flat IFC colour to the preset: triplanar albedo, roughness
// and normal map. Everything that differs between materials is a node property, because the
// compiled program is cached by the node graph, not by plain fields.
export class BatchMaterial extends MeshStandardNodeMaterial {
  private readonly own: Node<'float'>;
  private readonly uniforms: LayerUniforms;
  private readonly pbr: Node<'float'>;
  private readonly surface: Node<'vec3'>;
  private readonly surfaceAlpha: Node<'float'> | null;

  constructor(uniforms: LayerUniforms, state: THREE.Texture, opts: BatchLookOptions) {
    super();
    this.uniforms = uniforms;
    this.color.copy(opts.color);
    this.opacity = opts.opacity;
    this.roughness = opts.roughness;
    this.metalness = opts.metalness;
    this.emissive.copy(opts.emissive);
    this.emissiveIntensity = opts.emissiveIntensity;
    this.side = THREE.DoubleSide;
    const texel = stateTexel(state);
    const own = select(texel.a.lessThan(0), float(1), float(0));
    this.own = own;
    if (opts.pass === 'solid') this.maskNode = batchColor.a.greaterThanEqual(0.999);
    if (opts.pass === 'glass') this.maskNode = batchColor.a.lessThan(0.999);
    const { preset, maps } = opts;
    const pbr = uniforms.uPbr;
    this.pbr = pbr.mul(own);
    const tint = mix(color(preset.tint), batchColor.rgb, float(preset.keepColor));
    let roughness: Node<'float'> = float(preset.roughness);
    if (maps) {
      const tri = triplanar(preset.tiling);
      this.surface = tri.sample(maps.albedo).rgb.mul(tint);
      roughness = tri.sample(maps.roughness).r.mul(preset.roughness);
      if (preset.normalScale > 0)
        this.normalNode = mix(
          normalView,
          tri.normal(maps.normal, preset.normalScale),
          this.pbr,
        ).normalize();
    } else {
      this.surface = tint;
    }
    this.surfaceAlpha = preset.opacity != null ? float(preset.opacity) : null;
    this.roughnessNode = mix(float(0.7), mix(materialRoughness, roughness, pbr), own);
    this.metalnessNode = mix(float(0), mix(materialMetalness, float(preset.metalness), pbr), own);
    this.emissiveNode = mix(
      texel.rgb.mul(texel.a),
      vec3(materialEmissive).mul(diffuseColor.rgb),
      own,
    );
  }

  override setupDiffuseColor(builder: NodeBuilder): void {
    super.setupDiffuseColor(builder);
    diffuseColor.rgb.assign(mix(diffuseColor.rgb, this.surface, this.pbr));
    if (this.surfaceAlpha) diffuseColor.a.assign(mix(diffuseColor.a, this.surfaceAlpha, this.pbr));
    const { uTint, uTintAmount } = this.uniforms;
    diffuseColor.rgb.assign(mix(diffuseColor.rgb, uTint, uTintAmount.mul(this.own)));
  }

  // Fresnel rim shell; uGhost stays 0 on layers that never ghost, so the mix is a no-op there.
  override setupOutput(builder: NodeBuilder, outputNode: Node): Node {
    const output = outputNode as Node<'vec4'>;
    const g = this.uniforms.uGhost.mul(this.own);
    const fres = float(1).sub(positionViewDirection.dot(normalView).saturate()).pow(2.2);
    const ghosted = output.rgb.mul(0.12).add(RIM.mul(fres.mul(0.55).add(0.08)));
    const alpha = mix(output.a, fres.mul(0.3).add(0.04), g);
    return super.setupOutput(builder, vec4(mix(output.rgb, ghosted, g), alpha));
  }
}

// Vignette as pmndrs' non-Eskil variant: darkens towards the corners of the screen.
export function vignette(
  input: Node<'vec4'>,
  uv: Node<'vec2'>,
  offset: number,
  darkness: number,
): Node<'vec4'> {
  const d = uv.sub(0.5).length();
  const v = smoothstep(float(0.8), float(offset * 0.799), d.mul(darkness + offset));
  return vec4(input.rgb.mul(v), input.a);
}
